import React from 'react';
import DashboardTemplate from 'src/templates/DashboardTemplate';
import { Button, Flex, Form, Input, Popconfirm } from 'antd';
import { useForm, useWatch } from 'antd/es/form/Form';
import {
	useDeleteZapiConfigMutation,
	useGetZapiConfigsQuery,
	usePatchZapiConfigMutation,
	usePostZapiConfigMutation
} from 'src/redux/services/centralApi';
import { toast } from 'react-toastify';

interface IAddConfigForm {
	apiToken: string;
	apiInstance: string;
	securityToken: string;
	phoneNumber: string;
}

export default function ConfigWhatsappPage() {
	const [ addConfigForm, ] = useForm<IAddConfigForm>();

	const phoneNumberValue = useWatch('phoneNumber', addConfigForm);

	const [ postZapiConfig, postZapiConfigResult, ] = usePostZapiConfigMutation();

	const [ patchZapiConfig, patchZapiConfigResult, ] =
        usePatchZapiConfigMutation();

	const [ deleteZapiConfig, ] =
        useDeleteZapiConfigMutation();

	const { data: zapiConfigsData, } = useGetZapiConfigsQuery();

	const phoneMask = () => {
		let phoneNumber = phoneNumberValue?.replace(/\D/g, '');

		if (phoneNumber) {
			if (phoneNumber?.length > 11) {
				phoneNumber = phoneNumber?.replace(/(\d{11})(.*)/, '$1');
			} else {
				phoneNumber = phoneNumber
					?.replace(/^(\d{2})(\d)/g, '($1) $2')
					?.replace(/(\d)(\d{4})$/, '$1-$2');
			}
			addConfigForm.setFieldValue('phoneNumber', phoneNumber);
			phoneNumber?.length &&
			addConfigForm.validateFields([ 'phoneNumber', ]);
		}
	};

	const onAddConfigFormFinish = (data: IAddConfigForm) => {
		if (!zapiConfigsData) {
			postZapiConfig({
				apiInstance: data.apiInstance,
				apiToken: data.apiToken,
				phoneNumber: data.phoneNumber,
				securityToken: data.securityToken,
			})
				.unwrap()
				.then(() => {
					toast.success('Configuração adicionada com sucesso!');
				})
				.catch((reason) => {
					if (reason.status === 409) {
						toast.error('Configuração já existente!');
					} else {
						toast.error('Erro ao adicionar configuração!');
					}
				});
		} else {
			if (!zapiConfigsData?.clientId) return;
			patchZapiConfig({
				id: zapiConfigsData?.clientId as string,
				zapiConfig: {
					apiInstance: data.apiInstance,
					apiToken: data.apiToken,
					phoneNumber: data.phoneNumber,
					securityToken: data.securityToken,
				},
			})
				.unwrap()
				.then(() => {
					toast.success('Configuração atualizada com sucesso!');
				})
				.catch(() => {
					toast.error('Erro ao atualizar configuração!');
				});
		}
	};

	React.useEffect(() => {
		if (zapiConfigsData) {
			addConfigForm.setFieldsValue({
				apiToken: zapiConfigsData?.apiToken,
				apiInstance: zapiConfigsData?.apiInstance?.toString(),
				securityToken: zapiConfigsData?.securityToken,
			});
			addConfigForm.setFieldValue(
				'phoneNumber',
				zapiConfigsData?.phoneNumber
			);
		}
	}, [ zapiConfigsData, ]);

	React.useEffect(() => {
		phoneMask();
	}, [ phoneNumberValue, ]);

	return (
		<DashboardTemplate pageTitle="Configurar API do Whatsapp">
			<Form
				onFinish={onAddConfigFormFinish}
				layout="vertical"
				form={addConfigForm}
			>
				<Form.Item
					required
					hasFeedback
					tooltip={<>Insira o token da API do Whatsapp.</>}
					label="Token da API"
					name="apiToken"
					rules={[
						{
							min: 2,
							message: 'Insira um token válido.',
						},
						{
							max: 150,
							message:
                                'O token deve ter no máximo 150 caracteres.',
						},
						{ required: true, message: 'Campo obrigatório.', },
					]}
				>
					<Input />
				</Form.Item>
				<Form.Item
					required
					tooltip={
						<>Insira o endereço da instância da API do Whatsapp.</>
					}
					label="Instância da API"
					name="apiInstance"
					hasFeedback
					rules={[
						{
							min: 2,
							message: 'Insira um endereço válido.',
						},
						{
							max: 150,
							message:
                                'O endereço deve ter no máximo 150 caracteres.',
						},
						{ required: true, message: 'Campo obrigatório.', },
					]}
				>
					<Input />
				</Form.Item>
				<Form.Item
					required
					tooltip={
						<>Insira o token de segurança da API do Whatsapp.</>
					}
					label="Token de segurança da API"
					name="securityToken"
					hasFeedback
					rules={[
						{
							min: 2,
							message: 'Insira um token válido.',
						},
						{
							max: 150,
							message:
                                'O token de segurança deve ter no máximo 150 caracteres.',
						},
						{ required: true, message: 'Campo obrigatório.', },
					]}
				>
					<Input />
				</Form.Item>
				<Form.Item
					required
					tooltip={
						<>Insira o número do Whatsapp que será utilizado.</>
					}
					hasFeedback
					label="Número do Whatsapp"
					name="phoneNumber"
					rules={[
						{
							min: 2,
							message: 'Insira um número válido.',
						},
						{
							validator: (_, value) => {
								if (value.match(/\(\d{2}\) 9\d{4}-\d{4}/)) {
									return Promise.resolve();
								}
								return Promise.reject();
							},
							message: 'Insira um número válido.',
						},
					]}
				>
					<Input />
				</Form.Item>
				<Flex gap={10} justify="space-between">
					<Form.Item>
						<Button
							loading={
								patchZapiConfigResult.isLoading ||
								postZapiConfigResult.isLoading
							}
							type="primary"
							htmlType="submit"
						>
                            Enviar
						</Button>
					</Form.Item>
					<Form.Item>
						<Popconfirm
							title="Tem certeza que deseja excluir a configuração?"
							onCancel={() =>
								toast.info('Operação cancelada!', {
									autoClose: 1000,
								})
							}
							onConfirm={() => {
								deleteZapiConfig(
                                    
								)
									.unwrap()
									.then(() => {
										addConfigForm.resetFields();
										toast.success(
											'Configuração deletada com sucesso!'
										);
									})
									.catch(() => {
										toast.error(
											'Erro ao deletar configuração!'
										);
									});
							}}
						>
							{!!zapiConfigsData && (
								<Button type="primary" danger>
                                    Deletar configuração existente
								</Button>
							)}
						</Popconfirm>
					</Form.Item>
				</Flex>
			</Form>
		</DashboardTemplate>
	);
}
